import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import Icon from '@material-ui/core/Icon';
import Link from '@material-ui/core/Link';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import TableCell from '@material-ui/core/TableCell';
import TableRow from '@material-ui/core/TableRow';
import { FiberManualRecord } from '@material-ui/icons';
// Components
import { TooltipText } from '../tooltip-text';
// Ressources
import ObjectUtils from '../../utils/objectUtils';
import FormattingUtils from '../../utils/formattingUtils';
import { TableRequestPoint } from '../../redux/reducers/table';
import { clsx } from '../../utils/htmlUtils';
import { FormatType, HeadCell } from '../../local-types';

interface TableRowProps extends WithTranslation {
    datas: any[];
    rowClass: string;
    headers: HeadCell[];
    type: TableRequestPoint;
    handleNoPropagation: (e) => void;
}

const TableRows = (props: TableRowProps) => {
    const { headers, type, datas, t, handleNoPropagation, rowClass } = props;

    const classes = useStyles();

    const CLIP_MAX_LENGTH = 30;
    const FEATURE_CLIP = false;

    const clipLongText = (text: string): JSX.Element => {
        if (!text || typeof text !== 'string' || text.length < CLIP_MAX_LENGTH) {
            return <>{text ?? ''}</>;
        }
        return <TooltipText title={text} label={text.substring(0, CLIP_MAX_LENGTH / 2) + '...'} />;
    };

    if (datas.length < 1) {
        return (
            <TableRow tabIndex={-1} className={rowClass}>
                <TableCell colSpan={headers.length} className={classes.emptyRow}>
                    {t('empty')}
                </TableCell>
            </TableRow>
        );
    }

    return (
        <>
            {datas.map((row, index) => {
                return (
                    <TableRow hover tabIndex={-1} key={index} className={rowClass}>
                        {headers.map((hrow, hindex) => {
                            const value = ObjectUtils.getSubValueFromKey(row, hrow.id);
                            let formattedValue = FormattingUtils.format(value, hrow.type, t);
                            let view = <b className={classes.text}>{`${formattedValue ?? ''}`}</b>;
                            if (FEATURE_CLIP) {
                                view = <b className={classes.text}>{clipLongText(formattedValue)}</b>;
                            }
                            let customProps = {};

                            if (hindex === 0) {
                                view = <b className={classes.id}>{`${formattedValue ?? ''}`}</b>;
                            }

                            if (hrow.loaderCell) {
                                view = hrow.loaderCell(row, hrow.id);

                                if (view) {
                                    return (
                                        <TableCell
                                            key={hrow.id}
                                            className="tableCell"
                                            {...customProps}
                                            {...hrow.cellProps}
                                        >
                                            {view}
                                        </TableCell>
                                    );
                                }
                            }

                            if (hrow.formatCell) {
                                //TODO: also clip formatCell or not ?
                                formattedValue = hrow.formatCell(row);
                                view = <b className={classes.text}>{hrow.formatCell(row)}</b>;
                            }

                            if (hrow.action && hrow.type === FormatType.Picker) {
                                customProps = { onClick: (e) => e.stopPropagation() };
                                const options = hrow.pickerOptions;
                                const cellValue = hrow.formatCell ? hrow.formatCell(row) : value;
                                const native = options.find((o) => o.icon) != null;
                                const disabled =
                                    hrow.disabled ||
                                    options.length === 0 ||
                                    (options.length === 1 && options[0].value === '');
                                view = (
                                    <Select
                                        name={hrow.id}
                                        native={!native}
                                        disabled={disabled}
                                        defaultValue={cellValue}
                                        className={classes.select}
                                        onClick={handleNoPropagation}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            hrow.action(row, { [hrow.id]: val });
                                        }}
                                    >
                                        {options.map((option) =>
                                            native ? (
                                                <MenuItem key={option.value} value={option.value}>
                                                    {option.icon && (
                                                        <ListItemIcon>
                                                            <Icon fontSize="small">{option.icon}</Icon>
                                                        </ListItemIcon>
                                                    )}
                                                    {option.label}
                                                </MenuItem>
                                            ) : (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ),
                                        )}
                                    </Select>
                                );
                            }

                            if (hrow.action && hrow.type === FormatType.Chips) {
                                customProps = { onClick: (e) => e.stopPropagation() };
                                const options = hrow.chipsOptions;
                                view = (
                                    <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                        {options.map((option) => {
                                            const activated = hrow.chipsTest(row, formattedValue, option);
                                            return (
                                                <Chip
                                                    key={option.id}
                                                    icon={null}
                                                    disabled={hrow.disabled}
                                                    color={activated ? 'secondary' : 'default'}
                                                    label={option.name}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        hrow.action(row, {
                                                            [hrow.id]: {
                                                                ...option,
                                                                value: !activated,
                                                            },
                                                        });
                                                    }}
                                                    className={classes.chipSelect}
                                                />
                                            );
                                        })}
                                    </div>
                                );
                            }

                            if (hrow.type === FormatType.BooleanChips) {
                                customProps = { onClick: (e) => e.stopPropagation() };
                                const activated = hrow.chipsTest(row, formattedValue);
                                const options = hrow.chipsOptions;
                                view = (
                                    <div style={{ display: 'flex' }}>
                                        <Chip
                                            size="small"
                                            icon={
                                                <FiberManualRecord
                                                    className={
                                                        activated ? classes.chipIconValid : classes.chipIconInvalid
                                                    }
                                                />
                                            }
                                            disabled={hrow.disabled}
                                            label={activated ? options[0] : options[1]}
                                            className={clsx(
                                                activated ? classes.chipValid : classes.chipInvalid,
                                                activated ? 'chipValid' : 'chipInvalid',
                                            )}
                                            color="default"
                                        />
                                    </div>
                                );
                            }

                            if (hrow.action && hrow.type === FormatType.Boolean) {
                                customProps = { onClick: (e) => e.stopPropagation() };
                                view = (
                                    <Checkbox
                                        checked={value}
                                        value={hrow.id}
                                        disabled={hrow.disabled}
                                        onClick={handleNoPropagation}
                                        onChange={(e) => {
                                            ObjectUtils.setSubValueFromKey(hrow, hrow.id, !value);
                                            hrow.action(row, { [hrow.id]: !value });
                                        }}
                                    />
                                );
                            }
                            const isLink = type && type.handleRow && !hrow.noLink;
                            return (
                                <TableCell key={hrow.id} className="tableCell" {...customProps} {...hrow.cellProps}>
                                    {isLink && (
                                        <Link
                                            component={RouterLink}
                                            to={
                                                type && type.handleRow
                                                    ? type.handleRow(
                                                          row.id || ObjectUtils.getSubValueFromKey(row, headers[0].id),
                                                          row,
                                                      )
                                                    : ''
                                            }
                                        >
                                            {view || ''}
                                        </Link>
                                    )}
                                    {!isLink && view}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                );
            })}
        </>
    );
};

export default React.memo(withTranslation(['table'])(TableRows));

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        id: {
            color: theme.palette.colors.table.id,
        },
        text: {
            color: theme.palette.colors.table.text,
        },
        chip: {
            margin: theme.spacing(0.5),
            borderRadius: 4,
            fontWeight: 'bold',
            color: theme.palette.primary.contrastText,
            backgroundColor: theme.palette.secondary.main,
            '& .MuiSvgIcon-root': {
                color: theme.palette.primary.contrastText,
            },
        },
        chipSelect: {
            flex: '0 0 25%',
            margin: theme.spacing(0.5),
            borderRadius: 4,
            fontWeight: 'bold',
            color: theme.palette.primary.contrastText,
        },
        chipValid: {
            backgroundColor: '#e0f8e6',
            color: '#27d350',
        },
        chipIconValid: {
            color: '#27d350',
        },
        chipInvalid: {
            backgroundColor: '#fdeaec',
            color: '#f35261',
        },
        chipIconInvalid: {
            color: '#f35261',
        },
        select: {
            '& .MuiSelect-select:not([multiple]) option, .MuiSelect-select:not([multiple]) optgroup': {
                backgroundColor: 'white',
                color: theme.palette.primary.main,
            },
        },
        disabledRow: {
            backgroundColor: theme.palette.colors.table.disabled,
            '&:nth-of-type(odd)': {
                backgroundColor: theme.palette.colors.table.disabled,
            },
        },
        emptyRow: {
            textAlign: 'center',
        },
    }),
);
